import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Section from '@pelckmans/elementis/components/section';
import Tabs from '@pelckmans/elementis/components/tabs';
import Tooltip from '@pelckmans/elementis/components/tooltip';
import PlusIcon from '@pelckmans/elementis/icons/plus';

import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ListGroup from '@pelckmans/elementis/components/list-group';
import { TABS } from '..';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import EmptyState from '../../../shared-components/EmptyState';
import LoadingState from '../../../shared-components/LoadingState';
import {
  getLicenses,
  getLicenseTransfers,
  License,
  LICENSE_TRANSFER_DIRECTION,
  LicenseTransfer,
} from '../api/licenses';
import LicenseActions from './components/Actions';
import LicenseTransferActions from './components/LicenseTransferActions';
import IdentityContext from '../../../contexts/IdentityContext';
import Button from '@pelckmans/elementis/components/button';
import AddLicenseModal from '../../../shared-components/AddLicenseModal';
import ModalContext from '../../../contexts/ModalContext';
import { LicenseStatePill, LicenseSubjectPill, LicenseTransferPill, LicenseValidityPill } from './components/Pills';

const BASE = 'PAGES.PROFILE.LICENSES';

function MyLicensesContent() {
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState<License[] | undefined>(undefined);
  const [licenseTransfers, setLicenseTransfers] = useState<LicenseTransfer[]>([]);
  const handleError = useThrowAsyncError();
  const { identity } = useContext(IdentityContext);
  const { showModal } = useContext(ModalContext);

  useEffect(() => {
    let current = true;

    Promise.all([getLicenses(), getLicenseTransfers()])
      .then(([licenses, licenseTransfers]) => {
        if (current) {
          setLicenses(licenses);
          setLicenseTransfers(licenseTransfers);
        }
      })
      .catch(handleError);

    return () => {
      current = false;
    };
  }, [handleError]);

  async function refetchLicenses() {
    try {
      const licenses = await getLicenses();
      setLicenses(licenses);
    } catch (error) {
      handleError(error);
    }
  }

  async function refetchLicenseTransfers() {
    try {
      const licenseTransfers = await getLicenseTransfers();
      setLicenseTransfers(licenseTransfers);
    } catch (error) {
      handleError(error);
    }
  }

  async function onTransferAccepted() {
    await Promise.all([refetchLicenses(), refetchLicenseTransfers()]);
  }

  const incomingLicenseTransfers = licenseTransfers.filter(x => x.direction === LICENSE_TRANSFER_DIRECTION.RECEIVER);

  if (!licenses) return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING_STATE_A11Y_LABEL`)} />;

  if (licenses.length === 0 && incomingLicenseTransfers.length === 0)
    return (
      <EmptyState
        variant="list"
        headerTitle={t(`${BASE}.MY_LICENSES.EMPTY.TITLE`)}
        bodyText={t(`${BASE}.MY_LICENSES.EMPTY.BODY`)}
        aria-label={t(`${BASE}.MY_LICENSES.EMPTY_STATE_A11Y_LABEL`)}
      />
    );

  return (
    <Section
      title={t(`${BASE}.TITLE`)}
      actionGroupRole="group"
      actionGroupAriaLabel="add-license-actions"
      actions={
        <Button
          prominence="secondary"
          startIcon={<PlusIcon />}
          onClick={() => showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetchLicenses} />)}
        >
          {t(`${BASE}.MY_LICENSES.ADD_LICENSE`)}
        </Button>
      }
    >
      <ListGroup>
        <List emptyMessage={identity.isTeacherAlike ? t(`${BASE}.PENDING_LICENSES.LIST.EMPTY_MESSAGE`) : undefined}>
          <List.Header title={t(`${BASE}.PENDING_LICENSES.TITLE`)} titleTag="h3" prominent />
          {incomingLicenseTransfers.map(licenseTransfer => (
            <List.Item
              key={licenseTransfer.id}
              title={licenseTransfer.license.name}
              subText={t(`${BASE}.MY_LICENSES.SUBTEXT`, {
                isbn: licenseTransfer.license.isbn,
                licenseCode: licenseTransfer.license.licenseCode,
              })}
              pillGroupProps={{
                role: 'status',
                'aria-label': licenseTransfer.license.description,
                children: (
                  <>
                    <LicenseStatePill
                      key={licenseTransfer.license.id + '-state'}
                      license={licenseTransfer.license}
                      licenseTransfers={licenseTransfers}
                    />
                    <LicenseTransferPill
                      key={licenseTransfer.license.id + '-transfer'}
                      license={licenseTransfer.license}
                      licenseTransfers={licenseTransfers}
                    />
                    <LicenseValidityPill
                      key={licenseTransfer.license.id + '-validity'}
                      license={licenseTransfer.license}
                    />
                    <LicenseSubjectPill
                      key={licenseTransfer.license.id + '-subject'}
                      license={licenseTransfer.license}
                    />
                  </>
                ),
              }}
              actionArea={
                <LicenseTransferActions
                  transferId={licenseTransfer.id}
                  onAccept={onTransferAccepted}
                  onDecline={refetchLicenseTransfers}
                  refetchLicenseTransfers={refetchLicenseTransfers}
                />
              }
            />
          ))}
        </List>
        <List emptyMessage={t(`${BASE}.MY_LICENSES.LIST.EMPTY_MESSAGE`)}>
          <List.Header title={t(`${BASE}.MY_LICENSES.TITLE`)} titleTag="h3" prominent />
          {licenses.map(license => (
            <List.Item
              key={license.id}
              title={license.name}
              subText={t(`${BASE}.MY_LICENSES.SUBTEXT`, { isbn: license.isbn, licenseCode: license.licenseCode })}
              contentArea={
                license.replaced ? (
                  <Tooltip
                    truncation="wrapped"
                    anchor={
                      <Pill prominence="warning" tooltip>
                        {t(`${BASE}.MY_LICENSES.REPLACED.LABEL`)}
                      </Pill>
                    }
                  >
                    {t(`${BASE}.MY_LICENSES.REPLACED.TOOLTIP`)}
                  </Tooltip>
                ) : undefined
              }
              pillGroupProps={{
                role: 'status',
                'aria-label': license.description,
                children: (
                  <>
                    <LicenseStatePill
                      key={license.id + '-state'}
                      license={license}
                      licenseTransfers={licenseTransfers}
                    />
                    <LicenseTransferPill
                      key={license.id + '-transfer'}
                      license={license}
                      licenseTransfers={licenseTransfers}
                    />
                    <LicenseValidityPill key={license.id + '-validity'} license={license} />
                    <LicenseSubjectPill key={license.id + '-subject'} license={license} />
                  </>
                ),
              }}
              actionArea={
                <LicenseActions
                  license={license}
                  licenseTransfers={licenseTransfers}
                  refetchLicenseTransfers={refetchLicenseTransfers}
                />
              }
            />
          ))}
        </List>
      </ListGroup>
    </Section>
  );
}

export default function MyLicenses() {
  return (
    <Tabs.TabsContent>
      <Tabs.TabPanel id={TABS.LICENSES}>
        <MyLicensesContent />
      </Tabs.TabPanel>
    </Tabs.TabsContent>
  );
}
